/**
 * Remote support of agroNave terminals, technician side (agronave-app `remotesupport/PROTOCOL.md`).
 *
 * isoFarm decides what an account may do on a terminal; the terminal enforces it. The screen
 * runs end to end over WebRTC between the browser and the terminal, so isoFarm never sees it.
 */

/** What an account may do on a terminal. Names only ever grant: unknown ones are ignored. */
export const REMOTE_SUPPORT_PERMISSIONS = {
  SCREEN: 'screen',
  SCREEN_INPUT: 'screen-input',
  SHELL: 'shell',
} as const;

/** What the terminal reported about its software with its latest code request. */
export interface RemoteSupportSoftware {
  version: string;
  commit: string;
  platform: string;
  osVersion: string;
}

export interface RemoteSupportIceServer {
  urls: string[];
  username?: string;
  credential?: string;
}

/** The answer of `POST /v1/remote-access-connect`. */
export interface RemoteSupportGrant {
  sessionId: string;
  serial: string;
  signalingUrl: string;
  /** Opens one technician channel; sent in the first frame, never in a URL or a log. */
  ticket: string;
  iceServers: RemoteSupportIceServer[];
  permissions: string[];
  /** What the account would have if the terminal's software enforced it. */
  permissionsUnsupported: string[];
  software?: RemoteSupportSoftware;
}

/** Reasons `remote-access-connect` refuses a code with (403 body). */
export type RemoteSupportConnectRefusal =
  | 'CODE_NOT_RECOGNISED'
  | 'NOT_PERMITTED'
  | 'SESSION_OWNED';

/** Frames the technician channel carries, one JSON object per text frame. */
export type RemoteSupportServerFrame =
  | {
      type: 'joined';
      peerId: string;
      serial: string;
      hostKey: string;
      user: string;
    }
  | { type: 'answer'; sdp: string }
  | { type: 'candidate'; candidate: string; mid: string }
  | { type: 'host-key'; publicKey: string; user: string }
  | { type: 'permissions'; permissions: string[] }
  | { type: 'end'; reason: string };

export type RemoteSupportClientFrame =
  | { type: 'hello'; ticket: string }
  | { type: 'offer'; sdp: string }
  | { type: 'candidate'; candidate: string; mid: string }
  | { type: 'end' };
